/**
 * Bank balance-sheet lab — domain model.
 *
 * Pure, deterministic functions with no UI or storage dependencies, so the
 * teaching math can be tested in isolation. All amounts are USD. This is a
 * deliberately simplified model: no regulation, credit losses, taxes, or full
 * accrual accounting.
 */

export interface BankState {
  cash: number
  securities: number
  loans: number
  deposits: number
  borrowings: number
  equity: number
}

export type BankAction = 'receive-deposit' | 'make-loan' | 'buy-security'

export interface ApplyResult {
  ok: boolean
  state: BankState
  /** Set when the action was rejected; state is returned unchanged. */
  error?: string
}

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

export function formatUsd(amount: number): string {
  return USD.format(amount)
}

/** Fixed transaction size and the visible minimum cash floor. */
export const TXN_AMOUNT = 1_000_000
export const MIN_CASH = 500_000

/** Annual interest rates for the simplified income view. */
export const RATES = {
  loans: 0.06,
  securities: 0.03,
  cash: 0,
  deposits: 0.01,
  borrowings: 0.04,
} as const

/** Cedar River Community Bank — the fixed starting model (reconciled to $19M). */
export function initialBank(): BankState {
  return {
    cash: 2_000_000,
    securities: 3_000_000,
    loans: 14_000_000,
    deposits: 16_000_000,
    borrowings: 1_000_000,
    equity: 2_000_000,
  }
}

export function totalAssets(state: BankState): number {
  return state.cash + state.securities + state.loans
}

export function totalLiabilitiesAndEquity(state: BankState): number {
  return state.deposits + state.borrowings + state.equity
}

export function isBalanced(state: BankState): boolean {
  return totalAssets(state) === totalLiabilitiesAndEquity(state)
}

export interface Income {
  earned: number
  paid: number
  nii: number
}

export function income(state: BankState): Income {
  const earned =
    state.loans * RATES.loans + state.securities * RATES.securities + state.cash * RATES.cash
  const paid = state.deposits * RATES.deposits + state.borrowings * RATES.borrowings
  return { earned, paid, nii: earned - paid }
}

/** Near-term liquidity is modeled as cash on hand. */
export function liquidity(state: BankState): number {
  return state.cash
}

export function isEarningAssetAction(action: BankAction): boolean {
  return action === 'make-loan' || action === 'buy-security'
}

const BELOW_MIN_ERROR = `That would drop cash below the ${formatUsd(
  MIN_CASH,
)} minimum. Receive a deposit first to fund it.`

/** Apply a single transaction. Invalid actions return the state unchanged. */
export function applyAction(state: BankState, action: BankAction): ApplyResult {
  switch (action) {
    case 'receive-deposit':
      return {
        ok: true,
        state: { ...state, cash: state.cash + TXN_AMOUNT, deposits: state.deposits + TXN_AMOUNT },
      }
    case 'make-loan':
      if (state.cash - TXN_AMOUNT < MIN_CASH) return { ok: false, state, error: BELOW_MIN_ERROR }
      return {
        ok: true,
        state: { ...state, cash: state.cash - TXN_AMOUNT, loans: state.loans + TXN_AMOUNT },
      }
    case 'buy-security':
      if (state.cash - TXN_AMOUNT < MIN_CASH) return { ok: false, state, error: BELOW_MIN_ERROR }
      return {
        ok: true,
        state: {
          ...state,
          cash: state.cash - TXN_AMOUNT,
          securities: state.securities + TXN_AMOUNT,
        },
      }
  }
}

/** Rebuild state by replaying a list of actions from the initial bank. */
export function replay(actions: BankAction[]): BankState {
  return actions.reduce((state, action) => {
    const result = applyAction(state, action)
    return result.ok ? result.state : state
  }, initialBank())
}

/** Plain-language accounting effect for immediate feedback after an action. */
export function explain(action: BankAction): string {
  switch (action) {
    case 'receive-deposit':
      return 'Cash +$1M and Deposits +$1M. New funding arrives as cash (an asset) and an equal obligation to repay it (a liability). The sheet stays balanced and liquidity improves, though funding cost rises a little.'
    case 'make-loan':
      return 'Cash −$1M, Loans +$1M. You moved idle cash into a higher-yielding loan, so net interest income rises — but liquidity falls. The profitable move consumed cash.'
    case 'buy-security':
      return 'Cash −$1M, Securities +$1M. Cash becomes an earning security. Income rises modestly while liquidity falls.'
  }
}

/** True once at least one earning asset has been funded and cash is at/above the floor. */
export function isComplete(actions: BankAction[]): boolean {
  const state = replay(actions)
  return actions.some(isEarningAssetAction) && liquidity(state) >= MIN_CASH
}
